"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const Tokens_1 = require("./Tokens");
const Notifications_1 = require("./Notifications");
const gqlClient_1 = tslib_1.__importDefault(require("./gqlClient"));
const base_64_1 = require("base-64");
const apiErrorHandler_1 = require("./apiErrorHandler");
var RequestType;
(function (RequestType) {
    RequestType["QUERY"] = "QUERY";
    RequestType["MUTATE"] = "MUTATE";
})(RequestType || (RequestType = {}));
function deferredTask(handler, client) {
    let reject;
    const promise = new Promise((resolve, _reject) => tslib_1.__awaiter(this, void 0, void 0, function* () {
        reject = _reject;
        try {
            resolve(yield handler());
        }
        catch (e) {
            reject(e);
        }
    }));
    let res = promise;
    /* eslint-disable @typescript-eslint/ban-ts-ignore */
    // @ts-ignore
    res.reject = reject;
    res.client = client;
    return res;
}
exports.typenameSymbol = Symbol("__typename");
function responseCleaner(obj) {
    if (Array.isArray(obj)) {
        return obj.map(item => {
            if (typeof item === "object" || Array.isArray(item)) {
                item = responseCleaner(item);
            }
            return item;
        });
    }
    const resp = Object.assign({}, obj);
    Object.keys(resp).forEach(key => {
        if (typeof resp[key] === "object" && resp[key] !== null)
            resp[key] = responseCleaner(resp[key]);
        if (["__typename"].indexOf(key) > -1) {
            resp[exports.typenameSymbol] = resp[key];
            delete resp[key];
        }
    });
    return resp;
}
class FragmentRegistry {
    constructor() {
        this.registry = {};
    }
    addFragment(name, fragmentSupplier) {
        if (!this.registry[name])
            this.registry[name] = [];
        this.registry[name].push(fragmentSupplier);
    }
    getFragment(name) {
        if (!this.registry[name])
            throw new Error("Can't find fragment definition: " + name);
        return this.registry[name].map(supplier => supplier()).join("\n");
    }
}
class ApiClient {
    constructor(factoryOpts) {
        var _a, _b, _c;
        this.callErrorHandler = true;
        this.pendingQueries = new Set();
        this.tokensUpdatedSubscription = null;
        this.fragmentRegistry = new FragmentRegistry();
        this.fetchClient = (_a = factoryOpts.fetch, (_a !== null && _a !== void 0 ? _a : fetch));
        this.webSocketImpl = (_b = factoryOpts.webSocketImpl, (_b !== null && _b !== void 0 ? _b : undefined));
        this.url = factoryOpts.url;
        this.tokens = new Tokens_1.Tokens(factoryOpts.appStorage, factoryOpts.prefix || "api");
        this.notifications = new Notifications_1.Notifications(factoryOpts.notificationCB, factoryOpts.pendingNotificationTimeout);
        this.websocketOnly = (_c = factoryOpts.websocketOnly, (_c !== null && _c !== void 0 ? _c : false));
    }
    getClient() {
        if (this.client !== undefined)
            return this.client;
        this.client = gqlClient_1.default(this.url, {
            shortLiveToken: this.tokens.getShortLiveToken.bind(this.tokens),
            authentication: () => tslib_1.__awaiter(this, void 0, void 0, function* () {
                return {
                    longLiveToken: yield this.tokens.getLongLiveToken(),
                };
            }),
            websocketOnly: this.websocketOnly,
            fetch: this.fetchClient,
            webSocketImpl: this.webSocketImpl,
        });
        this.tokenRefresher.listenForTokensUpdated().then(subscription => (this.tokensUpdatedSubscription = subscription));
        return this.client;
    }
    shouldRefreshToken() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.websocketOnly)
                return false;
            const currentLongLiveToken = yield this.tokens.getLongLiveToken();
            if (!currentLongLiveToken)
                return false;
            const currentShortLiveToken = yield this.tokens.getShortLiveToken();
            const expireTime = currentShortLiveToken
                ? Number(JSON.parse(base_64_1.decode(currentShortLiveToken.split(".")[1])).exp + "000")
                : 0;
            return expireTime - new Date().getTime() < 1000 * 60;
        });
    }
    setCallErrorHandler(v) {
        this.callErrorHandler = v;
    }
    getCallErrorHandler() {
        return this.callErrorHandler;
    }
    static defaultError(status, message, response) {
        const getCode = (status) => {
            switch (status) {
                case 400:
                    return "BAD_REQUEST";
                case 401:
                    return "UNAUTHENTICATED";
                case 403:
                    return "FORBIDDEN";
                case 404:
                    return "PAGE_NOTFOUND";
                default:
                    return "UNKNOWN_ERROR";
            }
        };
        return {
            code: getCode(status),
            status: "error",
            message,
            error: new Error((response !== null && response !== void 0 ? response : message)),
            details: {
                propertyPath: "",
                className: "",
                message,
                messageTemplate: message,
            },
        };
    }
    static defaultErrorParser(status, response) {
        switch (status) {
            case 400:
                return ApiClient.defaultError(status, "Invalid input data", response);
            case 401:
                return ApiClient.defaultError(status, "Authentication required", response);
            case 403:
                return ApiClient.defaultError(status, "Access denied", response);
            case 404:
                return ApiClient.defaultError(status, "Not found", response);
            default:
                return ApiClient.defaultError(status, "Internal error", response);
        }
    }
    fetch(input, init, errorParser) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const notifier = this.notifications.startRequest();
            const callHandler = this.getCallErrorHandler();
            this.setCallErrorHandler(true);
            let res;
            let errors;
            try {
                res = yield this.fetchClient(input, init);
            }
            catch (e) {
                errors = [ApiClient.defaultError(0, e.toString())];
            }
            if (res && !res.ok) {
                if (errorParser) {
                    errors = yield errorParser(res);
                }
                else {
                    errors = [ApiClient.defaultErrorParser(res.status, yield res.text())];
                }
            }
            if (errors !== undefined) {
                notifier.requestFailed(errors, callHandler);
                throw errors;
            }
            notifier.requestFinished();
            return res;
        });
    }
    request(type, schema) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const notifier = this.notifications.startRequest();
            const callHandler = this.getCallErrorHandler();
            this.setCallErrorHandler(true);
            if (!schema.skipRefresh && (yield this.shouldRefreshToken())) {
                this.setCallErrorHandler(false);
                yield this.tokenRefresher.refreshToken(yield this.tokens.getLongLiveToken());
            }
            const client = this.getClient();
            try {
                let response;
                const task = deferredTask(() => tslib_1.__awaiter(this, void 0, void 0, function* () {
                    try {
                        if (type === RequestType.MUTATE)
                            return yield client.mutate(schema);
                        else
                            return yield client.query(schema);
                    }
                    finally {
                        this.pendingQueries.delete(task);
                    }
                }), client);
                this.pendingQueries.add(task);
                response = yield task;
                notifier.requestFinished();
                return responseCleaner(response);
            }
            catch (e) {
                const errors = apiErrorHandler_1.getApiErrors(e);
                notifier.requestFailed(errors, callHandler);
                if (errors.some(error => error.code === "UNAUTHENTICATED") && !schema.ignoreUnauthenticatedErrors) {
                    yield this.tokenRefresher.logout(client);
                }
                throw errors;
            }
        });
    }
    setupTokenRefresher(refresher) {
        this.tokenRefresher = refresher;
    }
    updateTokens(shortLiveToken, longLiveToken) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (shortLiveToken !== undefined)
                yield this.tokens.setShortLiveToken(shortLiveToken);
            if (longLiveToken !== undefined)
                yield this.tokens.setLongLiveToken(longLiveToken);
        });
    }
    getLongLiveToken() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.tokens.getLongLiveToken();
        });
    }
    query(schema) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return yield this.request(RequestType.QUERY, schema);
        });
    }
    mutate(schema) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return yield this.request(RequestType.MUTATE, schema);
        });
    }
    subscribe(schema, onData, onError) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                if (!this.websocketOnly) {
                    // dummy subscribe for HTTP connection
                    return Promise.resolve({
                        closed: true,
                        unsubscribe: () => { },
                    });
                }
                const client = this.getClient();
                return client
                    .subscribe(schema)
                    .map(event => {
                    if (event.errors !== undefined && event.errors.length > 0) {
                        throw event.errors[0];
                    }
                    return responseCleaner(event.data);
                })
                    .subscribe(onData, e => {
                    var _a;
                    let notifyErrorHandler = true;
                    // In case of onConnect throw error, we receive error encapsulated in message field.
                    if (typeof e.message === "object") {
                        e = e.message;
                    }
                    if (((_a = e.extensions) === null || _a === void 0 ? void 0 : _a.code) === "UNAUTHENTICATED") {
                        notifyErrorHandler = false;
                        this.tokenRefresher.logout(client);
                    }
                    if (onError) {
                        onError(apiErrorHandler_1.getApiErrors(e));
                    }
                    else {
                        if (notifyErrorHandler)
                            this.notifications.requestFailed(apiErrorHandler_1.getApiErrors(e));
                    }
                    this.close(client);
                });
            }
            catch (e) {
                // eslint-disable-next-line
                e = apiErrorHandler_1.getApiErrors(e);
                this.notifications.requestFailed(e);
                throw e;
            }
        });
    }
    close(client = this.client) {
        var _a;
        if (client) {
            client.stop();
            //@ts-ignore
            if (client.link.subscriptionClient) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                //@ts-ignore
                client.link.subscriptionClient.close();
            }
            (_a = this.tokensUpdatedSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
            this.tokensUpdatedSubscription = null;
        }
        if (this.client === client)
            this.client = undefined;
        this.pendingQueries.forEach(task => {
            if (task.client == client) {
                task.reject(new Error("Connection closed"));
                this.pendingQueries.delete(task);
            }
        });
    }
}
exports.ApiClient = ApiClient;
//# sourceMappingURL=ApiClient.js.map